import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Search, X, Link2, LayoutGrid, List, FileText, Eye, Plus,
  ChevronLeft, ChevronRight, Monitor, Tablet, Smartphone,
} from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'
import { BuilderBlockPreview, BuilderBlockPreviewFrame } from './BuilderBlockPreview'
import { useDialogFocus } from './hooks/useDialogFocus'
import { DEVICE_PREVIEW_WIDTHS } from './editable/responsiveVisibility'
import { notify } from '../../notices'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * « Ajouter un bloc existant » — réutiliser un bloc DÉJÀ POSÉ, par référence.
 *
 * Poser un bloc depuis le catalogue COPIE la graine de sa définition : deux
 * pages portent alors deux contenus indépendants. Ici on ne copie rien — la
 * page reçoit un porteur (`SharedBlock`) qui pointe vers l'unité choisie. Toutes
 * les places lisent donc la MÊME entité : éditer le bloc ici le change partout.
 * (ADR-001, quadrant « contenu partagé » : même dataRef = même contenu.)
 *
 * Pourquoi du JSX et pas du YAML (rituel config-first) : c'est du chrome
 * d'éditeur — une recherche d'entités qui monte des rjBuild inconnus à la volée
 * et pilote `addBlock` du contexte builder. Aucune capacité reactive-json ne
 * touche le state d'édition du canevas ; le bloc porteur, lui, est bien du YAML
 * déclaratif (`config/builder/blocks/SharedBlock.yaml`).
 *
 * Le fichier sert DEUX surfaces à partir de la même liste :
 *  - `BuilderExistingBlockPicker` — la pop-in du « + » (portail + scrim) ;
 *  - `editors/SharedUnitRefEditor` — l'éditeur du champ `shared-unit-ref`,
 *    qui réutilise `ExistingBlockList` nue dans le popover ancré à la pastille du
 *    porteur (pour en changer la cible).
 */

export const SHARED_CARRIER_TYPE = 'SharedBlock'

const PAGE_SIZE = 12
const COMPACT_PAGE_SIZE = 6

const PREVIEW_DEVICES = [
  { id: 'desktop', Icon: Monitor, label: 'Ordinateur' },
  { id: 'tablet', Icon: Tablet, label: 'Tablette' },
  { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
]

/** Le pseudo-bloc de registre qui monte l'unité `nid` telle qu'elle est. */
const previewBlockFor = (item) => ({
  type: `${item.type}-${item.nid}`,
  name: item.typeName || item.type,
  rjBuild: { renderView: [{ type: 'UnitMount', nid: item.nid }] },
  defaultData: {},
})

// ---------------------------------------------------------------------------
// La liste (sans chrome) — partagée par la pop-in et l'éditeur de champ
// ---------------------------------------------------------------------------

export const ExistingBlockList = ({ value, onPick, onPreview, compact = false }) => {
  const { page } = useBuilder()
  const size = compact ? COMPACT_PAGE_SIZE : PAGE_SIZE
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [from, setFrom] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  // Le partage est la raison d'être de cette liste : à défaut de choix, le bloc
  // déjà posé ailleurs passe devant (le serveur applique le même défaut).
  const [sort, setSort] = useState('usage')
  const [categories, setCategories] = useState([])
  // Le compte de « Toutes les catégories » : celui d'AVANT le filtre de
  // catégorie, donc pas `total` (qui décrit la liste servie, filtre compris).
  const [categoriesTotal, setCategoriesTotal] = useState(0)
  const [view, setView] = useState('list')
  const searchId = useId()

  // Les blocs de CETTE page ne sont pas proposés : les référencer depuis
  // eux-mêmes n'a pas de sens (et `UnitMount` couperait la récursion par un
  // encart d'erreur plutôt que par un rendu).
  const excluded = useMemo(
    () => page.blocs.map(b => b.nid).filter(Boolean).join(','),
    [page.blocs],
  )

  // Changer la recherche, le filtre ou le tri remet la pagination à zéro : rester
  // page 3 d'une requête qui n'a plus qu'une page rendrait une liste vide sans
  // rien dire.
  useEffect(() => { setFrom(0) }, [search, category, sort, excluded])

  // Recherche côté serveur, débattue : elle porte sur le CONTENU des blocs, ce
  // qu'aucun filtre local ne saurait faire — la bibliothèque d'un site se compte
  // en centaines de blocs, dont une page seulement est ici.
  useEffect(() => {
    const ctrl = new AbortController()
    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({ size: String(size), from: String(from) })
        if (search.trim()) params.set('q', search.trim())
        // Catégorie et tri sont résolus côté SERVEUR : la catégorie vit dans la
        // définition du bloc (pas dans la ligne servie) et le rang « le plus
        // partagé » se lit sur toute la bibliothèque, pas sur la page reçue.
        if (category) params.set('category', category)
        params.set('sort', sort)
        // On envoie la PAGE, jamais le site : c'est le serveur qui en déduit
        // le cloisonnement (la réponse porte du texte de contenu réel, un
        // paramètre de site optionnel se contournerait en l'omettant).
        if (page.pageNid) params.set('page', String(page.pageNid))
        if (excluded) params.set('exclude', excluded)
        const res = await fetch(`/api/builder/blocks/existing?${params}`, {
          credentials: 'include',
          signal: ctrl.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const body = await res.json()
        setItems(Array.isArray(body?.items) ? body.items : [])
        setTotal(Number(body?.pagination?.total ?? 0))
        // Le rail vient du serveur : ce sont les catégories RÉELLEMENT présentes
        // sous les autres filtres, comptées sur la liste entière — pas celles de
        // la page affichée, qui n'en montre qu'une douzaine.
        setCategories(Array.isArray(body?.categories) ? body.categories : [])
        setCategoriesTotal(Number(body?.categoriesTotal ?? 0))
      } catch (e) {
        if (e.name === 'AbortError') return
        console.error('Builder: existing-blocks fetch failed', e)
        setError('Impossible de charger les blocs existants.')
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, 250)
    return () => { clearTimeout(timer); ctrl.abort() }
  }, [search, category, sort, page.pageNid, excluded, from, size])

  // Une catégorie choisie qui disparaît du rail (une recherche l'a vidée) reste
  // proposée : sans son option, le menu paraîtrait vide alors que le filtre, lui,
  // s'applique toujours — et l'on ne pourrait plus le retirer.
  const categoryOptions = useMemo(
    () => (category && !categories.some(c => c.key === category)
      ? [...categories, { key: category, count: 0 }]
      : categories),
    [categories, category],
  )

  const pageCount = Math.max(1, Math.ceil(total / size))
  const current = Math.floor(from / size)

  const facetProps = {
    sort, setSort, category, setCategory, options: categoryOptions, total: categoriesTotal,
  }

  const searchBar = (
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-5 py-3">
      <div className="relative min-w-64 flex-1">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        <input
          id={searchId}
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher dans le contenu des blocs…"
          autoFocus
          className="input pl-9"
          aria-label="Rechercher parmi les blocs existants"
        />
      </div>
      {/* Le popover ancré n'a pas la largeur d'un rail : là, et là seulement,
          les mêmes facettes se replient en menus déroulants. */}
      {compact && <FacetSelects {...facetProps} />}

      {!compact && (
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1" role="group" aria-label="Mode d'affichage">
            <ViewToggle current={view} value="list" onChange={setView} icon={<List size={15} aria-hidden="true" />} label="Liste" />
            <ViewToggle current={view} value="render" onChange={setView} icon={<LayoutGrid size={15} aria-hidden="true" />} label="Rendu" />
          </div>
        </div>
      )}
    </div>
  )

  /* `role="status"` : la liste se recharge au fil de la frappe. Sans région
     live, un lecteur d'écran n'apprend ni le chargement, ni l'erreur, ni
     « aucun résultat » (WCAG 2.2 AA, 4.1.3 Status Messages). */
  const results = (
    <div
      role="status"
      aria-live="polite"
      aria-busy={loading || undefined}
      className={compact ? 'max-h-80 overflow-auto py-2' : 'modal-body min-h-0 flex-1'}
    >
      {error && <p className="px-2 text-sm text-rose-600">{error}</p>}

      {loading && (
        <p className="px-2 py-6 text-center text-sm text-slate-400">Chargement…</p>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="empty-state">
          <span className="empty-state-icon"><Link2 size={22} aria-hidden="true" /></span>
          <p className="font-semibold text-slate-700">Aucun bloc réutilisable</p>
          <p className="max-w-md text-slate-500">
            {search.trim()
              ? 'Aucun bloc existant ne correspond à cette recherche.'
              : category
                ? `Aucun bloc existant dans la catégorie « ${category} ».`
                : "Les blocs des autres pages apparaîtront ici une fois enregistrés. Les blocs de la page courante ne sont pas proposés."}
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        view === 'render' && !compact ? (
          <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item, idx) => (
              <div
                key={item.nid}
                className={`flex flex-col overflow-hidden rounded-lg border transition-colors ${
                  value?.nid === item.nid ? 'border-teal-400' : 'border-slate-200'
                }`}
              >
                <BuilderBlockPreviewFrame block={previewBlockFor(item)} index={idx} />
                <ItemMeta item={item} />
                <ItemActions item={item} onPick={onPick} onPreview={onPreview} />
              </div>
            ))}
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {items.map(item => (
              <li key={item.nid}>
                <div
                  className={`flex items-center gap-2 rounded-lg border pr-2 transition-colors ${
                    value?.nid === item.nid
                      ? 'border-teal-400 bg-teal-50'
                      : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
                  }`}
                >
                  {/* La ligne entière sélectionne ; l'aperçu est un bouton
                      FRÈRE, pas imbriqué (un bouton dans un bouton n'est ni
                      du HTML valide ni atteignable au clavier). */}
                  <button
                    type="button"
                    onClick={() => onPick(item)}
                    aria-current={value?.nid === item.nid ? 'true' : undefined}
                    className="min-w-0 flex-1 py-0.5 text-left"
                  >
                    <ItemMeta item={item} />
                  </button>
                  <ItemActions item={item} onPick={onPick} onPreview={onPreview} inline />
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  )

  const pager = !loading && !error && pageCount > 1 ? (
    <Pager
      current={current}
      pageCount={pageCount}
      total={total}
      onGo={(p) => setFrom(p * size)}
      compact={compact}
    />
  ) : null

  if (compact) {
    return <>{searchBar}{results}{pager}</>
  }

  // Le rail borde la LISTE, pas la pop-in : il reste en place pendant que le
  // volet de droite se recharge, et c'est le volet — pas la fenêtre — qui défile.
  return (
    <div className="flex min-h-0 flex-1">
      <FacetRail {...facetProps} />
      <div className="flex min-w-0 flex-1 flex-col">
        {searchBar}
        {results}
        {pager}
      </div>
    </div>
  )
}

/**
 * Les rangements de la bibliothèque : classer, puis restreindre à une catégorie.
 *
 * Deux surfaces, un seul état — d'où deux rendus du même jeu de facettes :
 *  - `FacetRail` — la colonne de gauche de la pop-in. Une entrée par ligne, son
 *    compte à droite : on voit d'un coup ce que contient la bibliothèque, et
 *    changer d'axe coûte un clic (un menu déroulant demande d'ouvrir pour voir).
 *  - `FacetSelects` — le repli du popover ancré (28 rem) : deux menus, qui
 *    tiennent la même place qu'il y ait une catégorie ou quinze.
 *
 * `usage` d'abord : le partage est la raison d'être de cette liste. Le serveur
 * applique le MÊME défaut — l'ordre n'est pas décidé deux fois.
 */
const SORTS = [
  { key: 'usage', label: 'Les plus partagés' },
  { key: 'name', label: 'Par nom (A → Z)' },
  { key: 'recent', label: 'Les plus récents' },
]

const FacetRail = ({ sort, setSort, category, setCategory, options, total }) => {
  const sortId = useId()
  const catId = useId()

  return (
    // `aside` et non `nav` : on ne navigue nulle part, on restreint une liste.
    // Chaque groupe porte son intitulé visible via `aria-labelledby` — sans quoi
    // « Structure » et « Les plus récents » arriveraient dans le même sac.
    <aside className="facet-rail w-56 shrink-0" aria-label="Trier et filtrer les blocs existants">
      <p className="facet-rail-heading" id={sortId}>Trier</p>
      <div role="group" aria-labelledby={sortId} className="flex flex-col gap-0.5">
        {SORTS.map(s => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSort(s.key)}
            aria-current={sort === s.key ? 'true' : undefined}
            className="facet-rail-item"
          >
            <span className="truncate">{s.label}</span>
          </button>
        ))}
      </div>

      <p className="facet-rail-heading" id={catId}>Catégorie</p>
      <div role="group" aria-labelledby={catId} className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={() => setCategory('')}
          aria-current={category === '' ? 'true' : undefined}
          className="facet-rail-item"
        >
          <span className="truncate">Toutes les catégories</span>
          <span className="facet-rail-count">{total}</span>
        </button>
        {options.map(c => (
          <button
            key={c.key}
            type="button"
            onClick={() => setCategory(c.key)}
            aria-current={category === c.key ? 'true' : undefined}
            className="facet-rail-item"
          >
            <span className="truncate">{c.key}</span>
            <span className="facet-rail-count">{c.count}</span>
          </button>
        ))}
      </div>
    </aside>
  )
}

const FacetSelects = ({ sort, setSort, category, setCategory, options }) => (
  <>
    {options.length > 0 && (
      <select
        value={category}
        onChange={e => setCategory(e.target.value)}
        className="select w-auto max-w-52"
        aria-label="Filtrer par catégorie de bloc"
        title="Filtrer par catégorie de bloc"
      >
        <option value="">Toutes les catégories</option>
        {options.map(c => (
          <option key={c.key} value={c.key}>{c.key} ({c.count})</option>
        ))}
      </select>
    )}

    <select
      value={sort}
      onChange={e => setSort(e.target.value)}
      className="select w-auto"
      aria-label="Trier les blocs existants"
      title="Trier les blocs existants"
    >
      {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  </>
)

/** L'identité d'un bloc pour un éditeur : son type, son texte, sa page. */
const ItemMeta = ({ item }) => (
  <div className="flex min-w-0 flex-col gap-1 px-3 py-2">
    <div className="flex min-w-0 items-center gap-2">
      <Link2 size={14} className="shrink-0 text-teal-600" aria-hidden="true" />
      <span className="truncate text-sm font-semibold text-slate-700">
        {item.excerpt || item.label}
      </span>
    </div>
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      <span className="tag">{item.typeName || item.type}</span>
      {item.page && (
        <span className="inline-flex items-center gap-1 truncate">
          <FileText size={12} aria-hidden="true" />
          {item.page.title}
        </span>
      )}
      {item.usage > 0 && (
        <span className="tag tag-iris">Déjà partagé · {item.usage}</span>
      )}
    </div>
  </div>
)

/** Aperçu / insertion. En liste, seul l'aperçu est un bouton dédié. */
const ItemActions = ({ item, onPick, onPreview, inline = false }) => (
  <div className={inline ? 'flex shrink-0 items-center gap-1' : 'flex items-center justify-end gap-2 border-t border-slate-100 px-3 py-2'}>
    {onPreview && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPreview(item) }}
        className="btn btn-ghost btn-sm"
        aria-label={`Prévisualiser « ${item.excerpt || item.label} »`}
        title="Prévisualiser ce bloc"
      >
        <Eye size={15} aria-hidden="true" />
        {!inline && <span>Aperçu</span>}
      </button>
    )}
    {!inline && (
      <button type="button" onClick={() => onPick(item)} className="btn btn-primary btn-sm">
        <Plus size={15} aria-hidden="true" />
        Ajouter
      </button>
    )}
  </div>
)

/**
 * Pagination fenêtrée. Le total vient du serveur (le compte de la requête, tous
 * filtres appliqués) — pas du nombre de lignes affichées, qui n'est qu'une page.
 */
const Pager = ({ current, pageCount, total, onGo, compact }) => {
  const window = compact ? 3 : 5
  const start = Math.max(0, Math.min(current - Math.floor(window / 2), pageCount - window))
  const pages = Array.from({ length: Math.min(window, pageCount) }, (_, i) => start + i)

  return (
    <nav
      aria-label="Pagination des blocs existants"
      className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-5 py-2.5"
    >
      <p className="text-xs text-slate-500">
        {total} bloc{total > 1 ? 's' : ''} · page {current + 1} sur {pageCount}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onGo(current - 1)}
          disabled={current === 0}
          className="btn btn-secondary btn-sm"
          aria-label="Page précédente"
        >
          <ChevronLeft size={15} aria-hidden="true" />
        </button>
        {pages[0] > 0 && <span className="px-1 text-xs text-slate-400" aria-hidden="true">…</span>}
        {pages.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => onGo(p)}
            aria-current={p === current ? 'page' : undefined}
            aria-label={`Page ${p + 1}`}
            className={p === current ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          >
            {p + 1}
          </button>
        ))}
        {pages.at(-1) < pageCount - 1 && <span className="px-1 text-xs text-slate-400" aria-hidden="true">…</span>}
        <button
          type="button"
          onClick={() => onGo(current + 1)}
          disabled={current >= pageCount - 1}
          className="btn btn-secondary btn-sm"
          aria-label="Page suivante"
        >
          <ChevronRight size={15} aria-hidden="true" />
        </button>
      </div>
    </nav>
  )
}

const ViewToggle = ({ current, value, onChange, icon, label }) => (
  <button
    type="button"
    onClick={() => onChange(value)}
    aria-pressed={current === value}
    className={current === value ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-ghost'}
    title={label}
  >
    {icon}
    <span className="sr-only">{label}</span>
  </button>
)

// ---------------------------------------------------------------------------
// La sur-pop-in d'aperçu — le bloc rendu tel qu'il sera, avant de l'insérer
// ---------------------------------------------------------------------------

const ExistingBlockPreviewModal = ({ item, onClose, onPick }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)
  const titleId = useId()
  const closeRef = useRef(null)
  const block = useMemo(() => previewBlockFor(item), [item])
  const [device, setDevice] = useState('desktop')

  useEffect(() => {
    const previous = document.activeElement
    closeRef.current?.focus()
    return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus() }
  }, [])

  return (
    <div
      className="modal-scrim modal-scrim-full modal-scrim-over"
      {...scrim}
    >
      <div
        className="modal-panel modal-panel-full"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">{item.excerpt || item.label}</h2>
            <span className="badge badge-neutral">{item.typeName || item.type}</span>
            {item.page && (
              <span className="inline-flex min-w-0 items-center gap-1 text-sm font-normal text-slate-500">
                <FileText size={13} aria-hidden="true" />
                <span className="truncate">{item.page.title}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1" role="group" aria-label="Largeur d'aperçu">
              {PREVIEW_DEVICES.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDevice(d.id)}
                  aria-pressed={device === d.id}
                  aria-label={d.label}
                  title={`Aperçu ${d.label.toLowerCase()} — ${DEVICE_PREVIEW_WIDTHS[d.id]} px`}
                  className={device === d.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                >
                  <d.Icon size={15} aria-hidden="true" />
                </button>
              ))}
            </div>
            <button type="button" ref={closeRef} onClick={onClose} className="btn btn-ghost" aria-label="Fermer l'aperçu">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="modal-body flex-1 space-y-4 bg-slate-50">
          {/* Le rendu est INERTE et retiré de l'arbre d'accessibilité — c'est une
              image du bloc, pas une interface. D'où ce texte de remplacement,
              sans quoi le dialogue serait muet en restitution vocale. */}
          <p className="sr-only">
            Aperçu du bloc « {item.excerpt || item.label} » (type {item.typeName || item.type}),
            avec son contenu réel, en largeur {DEVICE_PREVIEW_WIDTHS[device]} pixels.
            Ce contenu est commun à toutes les pages qui l'utilisent.
          </p>
          {/* Largeur EXACTE de l'appareil, sans mise à l'échelle : le responsive
              des blocs Orbit passe par des requêtes de CONTENEUR, donc comprimer
              l'aperçu lui ferait rendre une autre version que la vraie. */}
          <div className="overflow-x-auto">
            <div
              className="block-preview-live mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm"
              style={{ width: DEVICE_PREVIEW_WIDTHS[device] }}
              inert
              aria-hidden="true"
            >
              <BuilderBlockPreview block={block} lazy={false} width={DEVICE_PREVIEW_WIDTHS[device]} />
            </div>
          </div>

          <p className="card p-4 text-sm text-slate-600">
            <Link2 size={14} className="mr-1.5 inline text-teal-600" aria-hidden="true" />
            En l'ajoutant, la page pointera vers ce bloc plutôt que d'en copier le contenu :
            toute modification, ici ou ailleurs, se répercutera partout où il est posé.
            {item.usage > 0 && ` Il est déjà partagé à ${item.usage} autre${item.usage > 1 ? 's' : ''} endroit${item.usage > 1 ? 's' : ''}.`}
          </p>
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Fermer</button>
          <button type="button" onClick={() => onPick(item)} className="btn btn-primary">
            <Plus size={16} aria-hidden="true" />
            Ajouter ce bloc
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// La pop-in du « + »
// ---------------------------------------------------------------------------

export const BuilderExistingBlockPicker = ({ position, onClose }) => {
  const scrim = useScrimDismiss(onClose)
  const { addBlock, registry, page } = useBuilder()
  const titleId = useId()
  const panelRef = useRef(null)
  const [previewItem, setPreviewItem] = useState(null)
  // `aria-modal` ne piège pas le clavier : sans ça, Tab quitte la modale et
  // parcourt le canevas dessous. Rend aussi le focus à la pastille « + ».
  useDialogFocus(panelRef)

  const carrierDef = useMemo(
    () => registry.find(e => e.type === SHARED_CARRIER_TYPE),
    [registry],
  )

  const handlePick = useCallback((item) => {
    if (!carrierDef) {
      notify({
        type: 'error',
        message: "Le bloc « Bloc existant » n'est pas installé sur ce serveur (drush ob-import-blocks-def).",
      })
      return
    }
    // Le porteur, pas une copie : `sharedRef` est le seul contenu qu'il porte.
    addBlock({
      type: SHARED_CARRIER_TYPE,
      defaultData: {
        sharedRef: { nid: item.nid, type: item.type, label: item.excerpt || item.label },
      },
    }, position)
    onClose()
  }, [addBlock, carrierDef, position, onClose])

  // Une touche = un niveau : Échap ferme l'aperçu d'abord (il s'empile
  // par-dessus dans useEscapeClose), la pop-in ensuite.
  useEscapeClose(onClose)

  // Même raison que le catalogue : portail vers <body>, au-dessus d'un canevas
  // qui empile haut (navbar de bloc en z-[100], Assistant en z-[200]).
  return createPortal(
    <div className="modal-scrim modal-scrim-full" {...scrim}>
      <div
        ref={panelRef}
        className="modal-panel modal-panel-full"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        // Sur-pop-in ouverte → ce panneau sort du parcours clavier et de l'arbre
        // d'accessibilité. `aria-modal` seul ne suffit pas : sans `inert`, Tab
        // quitte l'aperçu et parcourt la recherche et les lignes de dessous.
        inert={previewItem ? true : undefined}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">Ajouter un bloc existant</h2>
            {page.siteId ? (
              <span className="tag tag-iris" title="Seuls les blocs de ce site sont proposés">
                Site : {page.siteName || `#${page.siteId}`}
              </span>
            ) : null}
            <span className="text-sm font-normal text-slate-500">
              Le contenu restera commun à toutes ses places
            </span>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost" aria-label="Fermer">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <ExistingBlockList value={null} onPick={handlePick} onPreview={setPreviewItem} />
      </div>

      {previewItem && (
        <ExistingBlockPreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onPick={handlePick}
        />
      )}
    </div>,
    document.body,
  )
}
